// ./src/ids.ts — client-generated identifiers.

/**
 * A RFC-4122 v4 UUID, in every browser.
 *
 * ⚠⚠ WHY THIS EXISTS AND WHY THE FALLBACK MATTERS. `crypto.randomUUID()` is only available in a
 * SECURE CONTEXT — https or localhost. Crew on a phone pointed at a laptop over the local network
 * (`http://192.168.x.x:3000`), or on an older browser, do not get it. Every hand-rolled copy of this
 * helper in the app fell back to something like `${Date.now()}-${Math.random()...}`, which is fine
 * for a React key and **not a UUID**.
 *
 * ⚠ That difference took the tally counter down. `useTallies` sent its fallback value as the tick's
 * idempotency key, `tally_entry.client_ref` is a `uuid` COLUMN, and Postgres refused it — rolling the
 * whole tick back, so the counter did not move and the operator saw a generic failure. It could only
 * ever happen off localhost, which is exactly where it is never tested. Found by
 * `livetests/tallyDrawdown.livetest.ts` in eventsmagic-api.
 *
 * ▶ Use this for any id the SERVER will store. A purely local React key does not need it.
 */
export const newUuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the manual builder below
  }

  // Manual v4 — random bytes where available, Math.random only as the last resort.
  const bytes = new Uint8Array(16);

  try {
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 16; i += 1) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
  } catch {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 4 and the RFC-4122 variant bits — without these it is a random string, not a UUID.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex: string[] = [];

  for (let i = 0; i < 16; i += 1) {
    hex.push(bytes[i].toString(16).padStart(2, "0"));
  }

  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};
